import React, {useCallback, useMemo} from "react"
import {PaginatedSelector} from "@/components/paginated-selector/paginated-selector";
import {countries} from "@/lib/countries";
import {PaginatedResponseV2} from "@/types/PaginatedResponse";
import {FilterQueryParams} from "@/components/paginated-selector/filters/types";

type Country = {
    id: string; // countryCode
    countryCode: string;
    countryNameEn: string;
    flag: string;
}

type CountrySelectorSimpleProps = {
    selectedIDs?: string[];
    setSelectedIDs?: (selectedIDs: string[]) => void;
}

export function CountrySelectorSimple({selectedIDs = [], setSelectedIDs}: CountrySelectorSimpleProps) {
    const headers = [
        "Country",
        "Code"
    ]

    // Convert country data to required format
    const countriesWithId: Country[] = useMemo(() => {
        return countries.map(country => ({
            id: country.countryCode,
            countryCode: country.countryCode,
            countryNameEn: country.countryNameEn,
            flag: country.flag
        }))
    }, [])

    const getCountries = useCallback(async (search: string, page: number, filterParams?: FilterQueryParams): Promise<PaginatedResponseV2<Country>> => {
        // Filter by search
        const searchLower = search.toLowerCase()
        const filtered = countriesWithId.filter(country =>
            country.countryNameEn.toLowerCase().includes(searchLower) ||
            country.countryCode.toLowerCase().includes(searchLower)
        )

        // Sort alphabetically by name
        filtered.sort((a, b) => a.countryNameEn.localeCompare(b.countryNameEn))

        // Paginate
        const pageSize = 10
        const start = (page - 1) * pageSize
        const end = start + pageSize
        const results = filtered.slice(start, end)

        return {
            results,
            totalResults: filtered.length,
            totalPages: Math.ceil(filtered.length / pageSize),
            currentPage: page
        }
    }, [countriesWithId])

    const getColumns = useCallback((country: Country) => {
        return [
            <span className="flex items-center gap-2">
                <span>{country.flag}</span>
                <span>{country.countryNameEn}</span>
            </span>,
            country.countryCode
        ]
    }, [])

    return <PaginatedSelector
        headers={headers}
        getItems={getCountries}
        getColumns={getColumns}
        selectedIDs={selectedIDs}
        setSelectedIDs={setSelectedIDs}
        disableSelect={false}
        showSelectionCount={true}
    />
}
